import React, { useContext } from 'react'
import LazyLoad from 'react-lazyload'
import { analytics } from '@helpers/analytics'
import { getLink, getCurrencyCode } from '@helpers/env'
import { getProductCategory } from '@helpers/products'
import { resizeShopifyImage } from '@helpers/shopify'
import { SHOW_COLLECTION_PRODUCT_TYPE } from '@consts/index'
import PriceComparison from '@components/ui/PriceComparison'
import CollectionTags from '@components/collection/CollectionTags'
import {
  CollectionListMeta,
  CollectionProduct as TypeCollectionProduct
} from 'types/collection'
import { ProductOption, ProductImage } from 'types/product'
import CollectionContext from '../collectionContext'
import styles from './styles.module.scss'

export type PlpRedesignAbTest = 'control' | 'variant-1' | 'variant-2'

const formatProductType = (productType: string) => {
  if (productType.indexOf('Shorts') > -1) return 'Shorts'
  if (productType.indexOf('Cord Sharps') > -1) return 'Sharps'
  if (productType.indexOf('Cord Fives') > -1) return 'Fives'

  return productType
}

type ProductDetailsProps = {
  product: TypeCollectionProduct
  title: string
  showProductType: boolean
}
function ProductDetails({
  product,
  title,
  showProductType
}: ProductDetailsProps) {
  return (
    <div className={styles.ProductDetails}>
      <h5 className={styles.product__color}>{product.title}</h5>
      {SHOW_COLLECTION_PRODUCT_TYPE.includes(title.toLowerCase()) && (
        <h5 className={styles.product__color}>{product.product_type}</h5>
      )}
      {showProductType && (
        <h5 className={styles.product__color}>
          {formatProductType(product.product_type)}
        </h5>
      )}

      {product.product_type !== 'Gift Card' && (
        <div className={styles.product__prices}>
          <PriceComparison
            price={product.price}
            fullPrice={product.compare_at_price}
            size="medium"
          />
        </div>
      )}
    </div>
  )
}

function getProductListImageIndex(
  productCategory: string | null,
  productType: string
): number {
  const matchesProductCategory = /Trousers|Shorts|Denim/.test(
    productCategory || ''
  )

  const isNotSwimsProductType = !/Swims/.test(productType)

  if (matchesProductCategory && isNotSwimsProductType) {
    return 2
  }

  return 1
}

type PrimaryImageProperties = {
  images: ProductImage[]
  productOptions: ProductOption[]
  productType: string
}

function plpPrimaryImage({
  images,
  productOptions,
  productType
}: PrimaryImageProperties) {
  const productCategory = getProductCategory(productOptions)

  return images[getProductListImageIndex(productCategory, productType)]
}

type CollectionProductProps = {
  product: TypeCollectionProduct
  title: string
  showProductType: boolean
  isTabletUp: boolean
}

const CollectionProduct = ({
  product,
  showProductType,
  title,
  isTabletUp
}: CollectionProductProps) => {
  const { collectionHandle } = useContext(CollectionContext)

  const productLink = getLink(`/products/${product.handle}`)
  const images = product.images

  const handleProductClick = () => {
    analytics('Product Clicked', {
      list_id: collectionHandle,
      context: 'Collection',
      category: getProductCategory(product.options),
      quantity: 1,
      name: product.product_type,
      color: product.title,
      price: parseFloat(product.price),
      url: productLink,
      image_url: product.image,
      brand: 'SPOKE',
      currency: getCurrencyCode()
    })
  }

  const primaryImage = plpPrimaryImage({
    images,
    productOptions: product.options,
    productType: product.product_type
  })

  return (
    <a
      className={styles.product}
      href={productLink}
      onClick={handleProductClick}
    >
      <CollectionTags tags={product.tags} />
      <div
        className={`${styles.product__imageContainer} ${styles.imagePlaceholder}`}
      >
        <div className={styles.product__image}>
          <LazyLoad height={275} once>
            {primaryImage && (
              <img
                className={`${styles.product__primaryImage} lazy`}
                src={resizeShopifyImage((primaryImage || {}).src || '', {
                  width: '395',
                  height: isTabletUp ? '395' : '593',
                  crop: 'center'
                })}
                alt={`${product.title} ${product.product_type} Primary`}
                width="183"
              />
            )}
            <img
              className={`${styles.product__secondaryImage} lazy`}
              src={resizeShopifyImage((images[0] || {}).src || '', {
                width: '395',
                height: isTabletUp ? '395' : '593',
                crop: 'center'
              })}
              alt={`${product.title} ${product.product_type} Secondary`}
              width="183"
            />
          </LazyLoad>
        </div>
      </div>
      <ProductDetails
        product={product}
        title={title}
        showProductType={showProductType}
      />
    </a>
  )
}

export default CollectionProduct
